/**
 * Toast notification component using Radix UI.
 *
 * Accessibility:
 * - Error toasts have role="alert" for assertive announcements
 * - Success/default toasts have role="status" for polite announcements
 * - Close button has aria-label for screen readers
 * - X icon is aria-hidden (decorative)
 */
import {
  forwardRef,
  type ReactNode,
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import * as ToastPrimitive from "@radix-ui/react-toast";
import { cn } from "@/lib/utils";
import {
  X,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Info,
  type LucideIcon,
} from "lucide-react";

// Toast context for global access
interface Toast {
  id: string;
  title?: string;
  description?: string;
  variant?: "default" | "success" | "error" | "warning";
}

interface ToastContextValue {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, "id">) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}

// Convenience methods
export function toast(options: { title?: string; description?: string }) {
  // This will be populated by ToastProvider
  window.__toastAdd?.({ ...options, variant: "default" });
}

toast.success = (options: { title?: string; description?: string }) => {
  window.__toastAdd?.({ ...options, variant: "success" });
};

toast.error = (options: { title?: string; description?: string }) => {
  window.__toastAdd?.({ ...options, variant: "error" });
};

toast.warning = (options: { title?: string; description?: string }) => {
  window.__toastAdd?.({ ...options, variant: "warning" });
};

// Extend window type
declare global {
  interface Window {
    __toastAdd?: (toast: Omit<Toast, "id">) => void;
  }
}

// Provider component
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((toast: Omit<Toast, "id">) => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts((prev) => [...prev, { ...toast, id }]);

    // Auto-remove after 4 seconds
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    window.__toastAdd = addToast;
    return () => {
      if (window.__toastAdd === addToast) {
        delete window.__toastAdd;
      }
    };
  }, [addToast]);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      <ToastPrimitive.Provider swipeDirection="right">
        {children}
        {toasts.map((t) => {
          const Icon = toastIcons[t.variant ?? "default"];
          return (
            <ToastRoot key={t.id} variant={t.variant}>
              <Icon className="size-4 shrink-0 mt-0.5" aria-hidden="true" />
              <div className="flex-1">
                {t.title && <ToastTitle>{t.title}</ToastTitle>}
                {t.description && (
                  <ToastDescription>{t.description}</ToastDescription>
                )}
              </div>
              <ToastClose onClick={() => removeToast(t.id)} />
            </ToastRoot>
          );
        })}
        <ToastViewport />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}

// Toast components
const ToastViewport = forwardRef<
  React.ElementRef<typeof ToastPrimitive.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Viewport
    ref={ref}
    className={cn(
      "fixed bottom-0 right-0 z-[100] flex max-h-screen w-full flex-col-reverse p-4 sm:bottom-4 sm:right-4 sm:flex-col sm:max-w-[420px]",
      className,
    )}
    {...props}
  />
));
ToastViewport.displayName = ToastPrimitive.Viewport.displayName;

const toastVariants = {
  default: "border-border-strong bg-surface-3 text-foreground",
  success: "border-positive-border bg-positive-tint text-positive",
  error: "border-negative-border bg-negative-tint text-negative",
  warning: "border-warning-border bg-warning-tint text-warning",
};

/** Leading status icon per variant so toasts are never color-only. */
const toastIcons: Record<keyof typeof toastVariants, LucideIcon> = {
  default: Info,
  success: CheckCircle,
  error: XCircle,
  warning: AlertTriangle,
};

interface ToastRootProps extends React.ComponentPropsWithoutRef<
  typeof ToastPrimitive.Root
> {
  variant?: keyof typeof toastVariants;
}

const ToastRoot = forwardRef<
  React.ElementRef<typeof ToastPrimitive.Root>,
  ToastRootProps
>(({ className, variant = "default", ...props }, ref) => (
  <ToastPrimitive.Root
    ref={ref}
    role={variant === "error" ? "alert" : "status"}
    aria-live={variant === "error" ? "assertive" : "polite"}
    className={cn(
      "group pointer-events-auto relative flex w-full items-start justify-between gap-3 overflow-hidden rounded-md border p-4 shadow-(--shadow-e3) transition-all",
      "data-[state=open]:animate-in data-[state=closed]:animate-out",
      "data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full",
      "data-[state=open]:slide-in-from-bottom-full sm:data-[state=open]:slide-in-from-bottom-full",
      toastVariants[variant],
      className,
    )}
    {...props}
  />
));
ToastRoot.displayName = ToastPrimitive.Root.displayName;

const ToastTitle = forwardRef<
  React.ElementRef<typeof ToastPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Title
    ref={ref}
    className={cn("text-sm font-semibold", className)}
    {...props}
  />
));
ToastTitle.displayName = ToastPrimitive.Title.displayName;

const ToastDescription = forwardRef<
  React.ElementRef<typeof ToastPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Description
    ref={ref}
    className={cn("text-sm opacity-90", className)}
    {...props}
  />
));
ToastDescription.displayName = ToastPrimitive.Description.displayName;

const ToastClose = forwardRef<
  React.ElementRef<typeof ToastPrimitive.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Close
    ref={ref}
    aria-label="Close notification"
    className={cn(
      "absolute right-2 top-2 rounded-md p-1 opacity-0 transition-opacity",
      "hover:opacity-100 focus:opacity-100 group-hover:opacity-100",
      "text-foreground/50 hover:text-foreground",
      className,
    )}
    toast-close=""
    {...props}
  >
    <X className="size-4" aria-hidden="true" />
  </ToastPrimitive.Close>
));
ToastClose.displayName = ToastPrimitive.Close.displayName;

export { ToastViewport, ToastRoot, ToastTitle, ToastDescription, ToastClose };
